/**
 * App composition root — TASK 7.2 + 7.3 + 7.4.
 *
 * Everything the three tasks build is wired together HERE, and `src/main.ts`
 * calls exactly one function from this file. That is deliberate: a module that
 * only a test can reach is dead code, so `Shell`, `PrimaryNav`, `MoreMenu`,
 * `OverlayStack`, `MiniPlayer`, `router`, `state-store`, `RouteAnnouncer`,
 * `LiveRegions`, `api/api-client.ts`, `lib/telemetry-client.ts`,
 * `lib/local-purge.ts`, `views/auth/*` and `views/settings/Settings.js` all
 * have their single production call site below.
 *
 * The mock's `componentDidMount` (L850-875) is the ancestor of {@link startApp}:
 * it seeded state from `steamy-data.js`, registered the `hashchange` listener,
 * and focused the first `<h1>`. What changed:
 *
 *   - the seed is DEV-ONLY (`import.meta.env.DEV` + dynamic import of
 *     `src/dev/mock-parity-fixture.js`); production reads the REAL session from
 *     `GET /api/me` and the REAL home page from `GET /api/home`;
 *   - `this.flags` and `_applyFlags(this._q())` are gone with the sim layer;
 *   - the 1s `setInterval` playback ticker is gone — real playback position
 *     comes from the `<audio>` element in TASK 10.x.
 *
 * ── BOOT ORDER (each step depends on the one before) ──────────────────────
 *   1. install the telemetry emitter — turns the shell's `session_started`
 *      no-op into a live path BEFORE any session can be gained;
 *   2. mount the shell;
 *   3. `normalizeEntryPath` — promote a PATH deep link (`/signin/denied`, the
 *      OAuth callback's own redirect target) into its hash route, BEFORE the
 *      router reads `location.hash`;
 *   4. load the session (`GET /api/me`) so the guard has truth, not a guess;
 *   5. consume any `returnTo` stashed before the Google round-trip;
 *   6. start the router — the guard now runs on every navigation.
 *
 * @module
 */

import { PLAY_EVENT_BATCH_MAX, QUEUE_ADD_BATCH_MAX, albumDetailResponseSchema } from '@streamymusic/shared';

import { apiClient, setOfflinePlayEventSink } from '../../api/api-client.js';
import { audioEngine } from '../../lib/audio-engine.js';
import { createDownloadManager } from '../../lib/download-manager.js';
import { createListDepthStore } from '../../lib/list-depth.js';
import { createWakeLock } from '../../lib/wake-lock.js';
import { mediaSession } from '../../lib/media-session.js';
import { MAX_PLAY_EVENTS, offlineStore } from '../../lib/offline-store.js';
import { setPrePurgeQuiesce } from '../../lib/local-purge.js';
import { setTelemetryBuffer, telemetryClient } from '../../lib/telemetry-client.js';
import { DownloadsView, QUOTA_WARNING_MESSAGE } from '../views/downloads/DownloadsView.js';
import { AlbumDetail } from '../views/album/AlbumDetail.js';
import { ArtistDetail } from '../views/browse/ArtistDetail.js';
import { AlbumsList } from '../views/browse/AlbumsList.js';
import { ArtistsList } from '../views/browse/ArtistsList.js';
import { HomeView } from '../views/home/HomeView.js';
import { StatsView } from '../views/stats/StatsView.js';
import { GenresList } from '../views/genres/GenresList.js';
import { GenrePage } from '../views/genres/GenrePage.js';
import { UsersView } from '../views/admin/UsersView.js';
import { SyncView } from '../views/admin/SyncView.js';
import { SearchView } from '../views/search/SearchView.js';
import { FavoritesView } from '../views/favorites/FavoritesView.js';
import { QueueView } from '../views/queue/QueueView.js';
import { PlaylistsList } from '../views/playlists/PlaylistsList.js';
import { PlaylistDetail } from '../views/playlists/PlaylistDetail.js';
import { Denied } from '../views/auth/Denied.js';
import { SignIn } from '../views/auth/SignIn.js';
import { SignInError } from '../views/auth/SignInError.js';
import {
  consumeReturnTo,
  guardRoute,
  normalizeEntryPath,
  RETURN_TO_PARAM,
} from '../views/auth/guard.js';
import { Settings } from '../views/settings/Settings.js';

import { ActionSheet, ConfirmModal } from './overlays.js';
import { mountShell } from './Shell.js';
import { PageHeading, createRouter, navigate } from './router.js';
import { getState, getTelemetryEmitter, setState, subscribe } from './state-store.js';

/**
 * How long an offline playback attempt is watched before it is reported as
 * failed (§11 `offline_play_attempted.succeeded`). Long enough for a cached
 * body to be read off disk and decoded on a slow device, short enough that the
 * event is not still pending when the user gives up and taps something else.
 */
export const OFFLINE_PLAY_OBSERVE_MS = 12_000;

/**
 * Trailing-edge window the download manager's `onChange` fan-out is coalesced
 * behind (Frontend/Performance W1).
 *
 * `onChange` fires on every status change AND every megabyte flushed, and each
 * one used to trigger a full Cache Storage enumeration, two `listTracks()`
 * calls and a `storage.estimate()` — the last of which makes WebKit walk the
 * origin's entire storage accounting, ~100 times per 100 MB track, on the same
 * thread draining the stream. 250 ms is below the threshold at which a progress
 * bar reads as laggy and far above the flush rate.
 */
export const PROGRESS_COALESCE_MS = 250;

/**
 * @typedef {object} AppHandle
 * @property {ReturnType<typeof mountShell>} shell
 * @property {ReturnType<typeof createRouter>} router
 * @property {import('../../lib/media-session.js').AutoAdvanceTier} mediaTier
 * @property {import('../../lib/download-manager.js').DownloadManager} downloads
 *   the queue this root wired, exposed so a suite can drive the SAME manager
 *   the composition root's `onChange` fan-out is attached to. Injecting one
 *   through `options.downloads` replaces that wiring, which is how the
 *   root -> view live-region seam went untested (W15).
 * @property {() => void} dispose
 */

/**
 * Builds the shell-less screen for a `shell:false` route.
 *
 * @param {{ route: { id: string }, query: URLSearchParams }} resolved
 * @param {Window} win
 * @returns {HTMLElement}
 */
function AuthScreen(resolved, win) {
  if (resolved.route.id === 'signin-denied') return Denied();
  if (resolved.route.id === 'signin-error') {
    return SignInError({ onRetry: () => navigate('#/signin') });
  }
  return SignIn({ returnTo: resolved.query.get(RETURN_TO_PARAM), win });
}

/**
 * Reads the REAL session from the server and writes it to the store.
 *
 * An `unknown` result (transport failure, 5xx) deliberately leaves the store
 * untouched: "the check did not complete" is not "signed out". Treating it as
 * a sign-out would bounce an offline PWA user to `/signin` on every cold start
 * and — worse, if it were ever wired to the purge — destroy their downloads.
 *
 * @param {{ getSession: () => Promise<{status: string, role?: 'admin'|'member'}> }} api
 * @returns {Promise<string>} the session status
 */
async function loadServerSession(api) {
  const result = await api.getSession();
  if (result.status === 'authenticated') {
    setState({
      session: {
        authenticated: true,
        role: result.role ?? null,
        // `GET /api/me` returns { userId, role, csrfToken } — no email or
        // display name exists to show. See Settings.js's "honest gap" note.
        user: null,
        // NOT for display. It is the owner stamp for locally-parked
        // PlayEvents — see state-store's `Session.userId` (Security W4).
        userId: result.userId ?? null,
      },
    });
  } else if (result.status === 'unauthenticated') {
    setState({ session: { authenticated: false, role: null, user: null, userId: null } });
  }
  return result.status;
}

/**
 * Boots the shell into `root` and starts routing.
 *
 * @param {{
 *   root: HTMLElement|null,
 *   win?: Window,
 *   doc?: Document,
 *   seed?: boolean,
 *   loadSession?: boolean,
 *   telemetry?: boolean,
 *   api?: typeof apiClient,
 *   player?: import('../../lib/audio-engine.js').AudioEngine,
 *   media?: import('../../lib/media-session.js').MediaSessionController,
 *   store?: import('../../lib/offline-store.js').OfflineStore,
 *   downloads?: import('../../lib/download-manager.js').DownloadManager,
 *   warn?: (message: string, detail: Record<string, string|number>) => void,
 *   wakeLock?: import('../../lib/wake-lock.js').WakeLockController,
 *   listDepth?: import('../../lib/list-depth.js').ListDepthStore,
 * }} options
 *   `seed` defaults to `import.meta.env.DEV`; pass `false` in tests that want
 *   an empty store. `loadSession` defaults to `!seed` — the DEV fixture
 *   supplies its own session, and tests that pre-seed the store pass `false`
 *   so no network call can clobber it.
 * @returns {Promise<AppHandle>}
 */
export async function startApp(options) {
  const {
    root,
    win = window,
    doc = document,
    seed = Boolean(import.meta.env.DEV),
    api = apiClient,
    player = audioEngine,
    media = mediaSession,
    store = offlineStore,
    warn = (message, detail) => {
      console.warn(message, detail);
    },
    loadSession = !seed,
    telemetry = true,
    wakeLock = createWakeLock({ warn }),
    // #back — persistent (24h) memory of how deep each catalog list was left.
    // Injectable so a suite gets an isolated store instead of sharing the
    // origin's real one across tests.
    listDepth = createListDepthStore(),
  } = options;

  if (root === null) {
    throw new Error('startApp: no mount point — index.html must contain <div id="app">');
  }

  // 1. Telemetry emitter first: the shell resolves it at CALL time, but
  //    installing here guarantees the cold-start `session_started` is captured.
  const uninstallTelemetry = telemetry ? telemetryClient.install() : () => {};

  // Keep the screen awake for the whole time the app is open (feature request
  // #1). This is the real answer to "playback stalls when the phone locks":
  // rather than fight iOS throttling a locked screen, we ask the OS not to lock
  // while the user is in the app, so the audio + JS layer never get suspended.
  // Best-effort — a device that refuses the lock simply behaves as before.
  wakeLock.setDesired(true);

  // 2. Shell.
  const shell = mountShell({
    root,
    getState,
    player,
    // The full player's ♥ and ⋯ need the api-client and the overlay stack —
    // both of which live HERE, not in the shell.
    onFavoriteCurrent: () => {
      const current = getState().playback.current;
      if (current !== null) void toggleFavorite('track', current.id);
    },
    onTrackActionsForCurrent: () => {
      const current = getState().playback.current;
      if (current !== null) {
        // Carry albumId/artistId (#8) so the player ⋯ can jump to the album or
        // the artist. `durationS` is deliberately omitted — the full-player
        // track has no reliable duration, which is what gates the download item.
        albumHandlers.onTrackActions({
          id: current.id,
          title: current.title,
          albumId: current.albumId,
          ...(current.artistId === undefined ? {} : { artistId: current.artistId }),
        });
      }
    },
    // The full player's lyrics panel fetches through the SAME api-client every
    // other view uses. The panel resolves the nothing-playing case itself (it
    // never calls this with a null id), so this stays a plain single GET.
    onFetchLyrics: (trackId) => api.getTrackLyrics(trackId),
    go: navigate,
    win,
    doc,
  });
  const unsubscribe = subscribe((state) => {
    shell.syncState(state);
    syncMediaSession(state);
  });

  /* ── Media Session (AC 6.6) ───────────────────────────────────────────
   *
   * BEST-EFFORT by contract (AC 6.9): the module no-ops where the API is
   * absent and skips individual actions the engine refuses. The in-app
   * controls never route through it.
   *
   * `play`/`pause` are guarded rather than mapped onto `toggle()` — the OS
   * sends the ABSOLUTE intent, so a `play` that arrived while already playing
   * must not pause.
   */
  media.setHandlers({
    // ABSOLUTE intents, straight to the engine's element-truth play/pause. The
    // old versions gated on `getState().playback.playing`, which goes stale when
    // iOS throttles JS behind a locked screen — so a lock-screen tap consulted
    // the wrong flag and did nothing. `player.play()`/`player.pause()` read the
    // audio element's real `paused` state, so they are always correct.
    play: () => player.play(),
    pause: () => player.pause(),
    // TASK 8.4 — the FALLBACK, shipped unconditionally. On an engine whose
    // background auto-advance is only best-effort (iOS/iPadOS), this is the
    // guaranteed way to reach the next track from a locked screen; on every
    // other engine it is simply an extra control. It costs nothing either way,
    // which is why it does not wait on the device spike
    // (docs/dev_plans/ios-spike-report.md).
    nexttrack: () => player.next(),
    previoustrack: () => player.previous(),
    seekto: (seconds) => player.seek(seconds),
    seekbackward: (offset) => player.seek(getState().playback.pos - offset),
    seekforward: (offset) => player.seek(getState().playback.pos + offset),
  });

  /*
   * The one case worth reporting: an engine that cannot promise background
   * auto-advance AND would not take the lock-screen control either. That
   * leaves the user with no out-of-app way forward, and it must not be
   * discovered from a bug report.
   */
  if (media.autoAdvanceTier === 'best-effort' && media.supported && !media.nextTrackRegistered) {
    warn('media-session: no lock-screen next-track control on a best-effort engine', {
      registered: media.registeredActions.join(','),
    });
  }

  /** Only re-publish METADATA on a track change; position on every tick. */
  let publishedTrackId = null;

  /** @param {import('./state-store.js').AppState} state */
  function syncMediaSession(state) {
    const current = state.playback.current;
    if (current === null) {
      if (publishedTrackId !== null) {
        media.setMetadata(null);
        publishedTrackId = null;
      }
      media.setPlaybackState('none');
      return;
    }
    if (current.id !== publishedTrackId) {
      publishedTrackId = current.id;
      media.setMetadata({
        title: current.title,
        artist: current.artist,
        album: current.album,
        // TASK 5.3's authed cover endpoint. An album with no stored cover
        // answers 404 and the OS simply shows nothing — which is why this is
        // offered rather than gated on a `coverRef` the queue item never
        // carries.
        artwork: [{ src: `/api/art/${encodeURIComponent(current.albumId)}` }],
      });
    }
    media.setPlaybackState(state.playback.playing ? 'playing' : 'paused');
    // `playbackRate` is what lets iOS ADVANCE the lock-screen scrubber on its
    // own between updates — without it the OS cannot extrapolate, so while JS is
    // throttled behind a locked screen the time appears frozen. 1 while playing,
    // 0 while paused, so the scrubber moves exactly when the audio does.
    media.setPositionState({
      duration: state.playback.dur,
      position: state.playback.pos,
      playbackRate: state.playback.playing ? 1 : 0,
    });
  }

  /* ── Story-8 catalog surfaces ─────────────────────────────────────────
   *
   * `renderRoute` swaps the DOM, but a view can also hold a store
   * subscription and an in-flight fetch, and only the view knows about those.
   * So every catalog view returns `{ element, dispose }` and the root disposes
   * the previous one before mounting the next — no listener outlives its
   * section, no late response paints into a page the user has left.
   *
   * @type {{ dispose: () => void }|null}
   */
  let activeView = null;

  /*
   * IN-APP NAVIGATION DEPTH (#back).
   *
   * How many route renders this app instance has performed. It is what tells a
   * "← Back" affordance whether there is anywhere of OURS to go back to: 1 means
   * the user landed here directly (cold start, deep link, PWA launch, the OAuth
   * return) and `history.back()` would leave the app entirely; more than 1 means
   * a previous route of ours is one entry behind.
   *
   * Counted here rather than read from `history.length`, which is per-TAB and
   * includes entries from before the app was opened as well as the overlay
   * stack's own sentinels — it cannot answer "is the previous entry mine".
   */
  let renderedRoutes = 0;
  const canGoBack = () => renderedRoutes > 1;

  /* ── Offline signal + the download set (TASK 10.1) ────────────────────
   *
   * `navigator.onLine` seeds `offline` and the window's `online`/`offline`
   * events keep it current; each catalog view receives the value it mounts
   * with, so a mid-view flip shows on the next navigation.
   *
   * The DOWNLOAD SET is no longer a deferral. `downloadedAlbums` is refreshed
   * from CACHE STORAGE (not from the download records — see
   * `download-manager.js#downloadedTrackIds` for why the bytes, not the
   * bookkeeping, are the truth) and handed to every catalog view, which is
   * what finally lets `AvailabilityBadge` tell "not downloaded" from
   * "downloaded" instead of marking everything unavailable offline.
   */
  let offline = win.navigator.onLine === false;

  /* Persistent queues move into the ONE database TASK 10.1 owns. Both of
   * these are setter seams rather than imports — see each module's header. */
  const restoreTelemetryBuffer = setTelemetryBuffer(store.telemetryBuffer);
  const restorePlayEventSink = setOfflinePlayEventSink({
    /**
     * AC 13.7 — a play that could not be delivered is parked locally and
     * merged on reconnect. `source` is rewritten to `'offline'` ONLY when the
     * runtime is certain there was no network: the audio engine stamps every
     * event `'stream'`, and a transport failure while online is a failed
     * upload of a streamed play, not an offline play.
     *
     * @param {readonly import('@streamymusic/shared').PlayEventInput[]} events
     */
    park: (events) =>
      store.appendPlayEvents(
        win.navigator.onLine === false
          ? events.map((event) => ({ ...event, source: 'offline' }))
          : events,
        // Security W4 — bound to the session that CAPTURED the play. Without
        // it, a device that changes user merges one person's listening history
        // into another's account at the next boot flush.
        getState().session.userId,
      ),
  });

  /** @type {string[]} */
  let downloadedAlbums = [];
  /**
   * Track ids with a COMPLETE cached body (Dev/Product W1).
   *
   * `downloadedAlbums` is a whole-album roll-up and cannot answer a per-ROW
   * question: it admits an album only when EVERY track is cached, so one failed
   * track out of ten made the nine cached ones render "⌒ not downloaded" and
   * refuse to play. The converse bites the moment single-track downloads exist
   * (they now do — see the track action sheet): one downloaded track would mark
   * the whole album downloaded and turn the other nine into silent stream
   * failures offline, which is exactly the AC 13.5 defeat the badge exists to
   * prevent. Grids keep the album roll-up; track rows get this.
   *
   * @type {string[]}
   */
  let downloadedTracks = [];
  /** The `/downloads` view while it is mounted, so progress can repaint it. */
  let downloadsView = null;

  /* ── Progress fan-out coalescing (Frontend/Performance W10) ───────────
   *
   * `onChange` fires on every status change AND every 1 MiB flushed. Fanning
   * it out synchronously meant, per megabyte: `cachedTrackIds()` (a full Cache
   * Storage enumeration), `listTracks()` TWICE, `listItems()`, and
   * `storage.estimate()` — the expensive one, because WebKit walks the whole
   * origin's storage accounting for it. That is ~100 rounds of it for a 100 MB
   * track, on the same thread draining the stream.
   *
   * So: coalesce behind a trailing timer, and skip the Cache Storage
   * enumeration entirely unless a track actually reached `done` — a mere
   * progress flush cannot change which bodies are complete, which is the only
   * thing `refreshDownloadSets` reads.
   */

  /** @type {ReturnType<typeof setTimeout>|null} */
  let coalesceTimer = null;
  /** Set when a transition might have changed the CACHED set, not just bytes. */
  let downloadSetsDirty = true;

  function runProgressFanOut() {
    coalesceTimer = null;
    if (downloadSetsDirty) {
      downloadSetsDirty = false;
      void refreshDownloadSets();
    }
    void downloadsView?.refresh();
  }

  /**
   * @param {{ completed?: boolean }} [detail] `completed` marks a transition
   *   that can change the cached set; a plain progress flush cannot.
   */
  function scheduleProgressFanOut(detail = {}) {
    if (detail.completed === true) downloadSetsDirty = true;
    if (coalesceTimer !== null) return;
    coalesceTimer = win.setTimeout(runProgressFanOut, PROGRESS_COALESCE_MS);
  }

  const downloads =
    options.downloads ??
    createDownloadManager({
      store,
      // LATE-BOUND through the store's emitter seam, not a direct
      // `telemetryClient` reference: that is the one emitter a test installs
      // (`setTelemetryEmitter`), and `startApp({telemetry:false})` must still
      // be able to observe `download_completed` / `download_failed`.
      telemetry: {
        emit: (event, payload) => {
          getTelemetryEmitter().emit(event, payload);
        },
      },
      onChange: (detail) => {
        scheduleProgressFanOut(detail);
      },
      isOnline: () => win.navigator.onLine !== false,
      warn,
    });

  async function refreshDownloadSets() {
    try {
      // ONE Cache Storage enumeration feeds both sets — `downloadedAlbumIds`
      // reads the same `cachedTrackIds()` internally, so asking for them
      // separately would walk the cache twice per refresh (W10).
      const [albums, tracks] = await Promise.all([
        downloads.downloadedAlbumIds(),
        downloads.downloadedTrackIds(),
      ]);
      downloadedAlbums = albums;
      downloadedTracks = tracks;
    } catch {
      /* a storage fault must not stop the app rendering; the badge just
         stays on its safe "not downloaded" default */
    }
  }

  /**
   * AC 13.7 — merges everything parked while offline through the IDEMPOTENT
   * `POST /api/play-events` batch (TASK 6.5). Each event carries its own
   * `clientEventId`, which is the server's dedupe key, so a flush that is
   * interrupted after the write but before the local delete cannot
   * double-count: the replay comes back `stored: 0, duplicates: n`.
   *
   * A failed flush RESTORES the batch. `appendPlayEvents` keys on
   * `clientEventId` too, so restoring a batch that was partly re-parked
   * elsewhere still leaves exactly one row per event.
   *
   * @returns {Promise<void>}
   */
  async function flushOfflinePlayEvents() {
    if (win.navigator.onLine === false) return;
    const ownerId = getState().session.userId;
    /*
     * LOOPS UNTIL THE STORE IS EMPTY (Dev/Performance W4).
     *
     * `drainPlayEvents` takes `PLAY_EVENT_BATCH_MAX` (50) — the server's batch
     * ceiling — while the store holds up to `MAX_PLAY_EVENTS` (500). Draining
     * ONCE meant a 300-event flight delivered 50 and stranded 250 until some
     * later `online` transition that, for a device that simply stayed online,
     * never comes. AC 13.7 says "merged on reconnect", not "the first 50 of
     * them are".
     *
     * Bounded by construction: `MAX_PLAY_EVENTS / PLAY_EVENT_BATCH_MAX` rounded
     * up is the most batches that can exist, and the `+ 1` covers events parked
     * WHILE the flush runs. Stops on the first failure so the restore path
     * still holds the batch that did not land.
     */
    const maxBatches = Math.ceil(MAX_PLAY_EVENTS / PLAY_EVENT_BATCH_MAX) + 1;
    for (let batch = 0; batch < maxBatches; batch += 1) {
      const parked = await store.drainPlayEvents(PLAY_EVENT_BATCH_MAX, ownerId);
      if (parked.length === 0) return;
      try {
        await api.sendPlayEvents(parked);
      } catch {
        await store.restorePlayEvents(parked, ownerId);
        return;
      }
    }
  }

  /*
   * SECURITY C1 — the download manager must be STOPPED before any purge, and
   * both purge callers (sign-out, and the AC 2.5 403 revocation reflex) reach
   * it through `lib/local-purge.ts#purgeLocalState`. Installing the quiesce on
   * that one function is what makes the guarantee structural rather than
   * something each call site has to remember. See `setPrePurgeQuiesce`.
   */
  const restorePrePurgeQuiesce = setPrePurgeQuiesce(async () => {
    // Cancels the queue, aborts the in-flight fetch, latches the manager shut
    // so no late continuation can re-create the database or the caches, and
    // closes the store connection so `deleteDatabase` is not `blocked`.
    await downloads.abortAll();
    if (coalesceTimer !== null) {
      win.clearTimeout(coalesceTimer);
      coalesceTimer = null;
    }
    // The sets describe a user who is about to stop being the signed-in one.
    downloadedAlbums = [];
    downloadedTracks = [];
  });

  const handleOnline = () => {
    offline = false;
    void flushOfflinePlayEvents();
  };
  const handleOffline = () => {
    offline = true;
  };
  win.addEventListener('online', handleOnline);
  win.addEventListener('offline', handleOffline);
  const uninstallDownloads = downloads.install(win);

  /* ── offline_play_attempted (§11 W17, the Offline-success SLI) ────────
   *
   * `succeeded` has to be TRUE, not assumed. A refused tap reports `false`
   * immediately (we already know); a tap that starts is WATCHED through the
   * store until the transport either reaches `playing` or paints an error
   * banner, and reported then. At most one attempt is under observation —
   * a second tap supersedes the first, which is what the user did too.
   */

  /** @type {(() => void)|null} */
  let cancelOfflineObserver = null;

  /**
   * @param {string} trackId
   * @param {boolean} succeeded
   */
  function reportOfflinePlay(trackId, succeeded) {
    // No `user_id`: §11's PII note puts it on the server, and the client's
    // allowlist (`EVENT_PROPERTIES`) would drop it anyway.
    getTelemetryEmitter().emit('offline_play_attempted', { track_id: trackId, succeeded });
  }

  /** @param {string} trackId */
  function observeOfflinePlay(trackId) {
    cancelOfflineObserver?.();
    let settled = false;
    /** @param {boolean} succeeded */
    const finish = (succeeded) => {
      if (settled) return;
      settled = true;
      cancelOfflineObserver = null;
      unwatch();
      win.clearTimeout(timer);
      reportOfflinePlay(trackId, succeeded);
    };
    const unwatch = subscribe((state) => {
      const current = state.playback.current;
      // The user moved on, or the engine dropped the track — either way this
      // attempt did not succeed.
      if (current === null || current.id !== trackId) {
        finish(false);
        return;
      }
      if (state.playback.playing) {
        finish(true);
        return;
      }
      const banner = state.playback.banner;
      if (banner !== null && banner.kind !== 'buffering') finish(false);
    });
    const timer = win.setTimeout(() => {
      finish(false);
    }, OFFLINE_PLAY_OBSERVE_MS);
    cancelOfflineObserver = () => {
      settled = true;
      unwatch();
      win.clearTimeout(timer);
    };
  }

  /* ── The AC 13.6 quota gate's user-visible half ───────────────────────
   *
   * The manager REFUSES; this decides what the user sees. The refusal is
   * toasted (which the shell also mirrors into the polite live region) on
   * whatever screen the user pressed Download, and latched so the `role=alert`
   * banner is standing when they follow it to `/downloads`. It clears on the
   * next successful enqueue — the condition it describes is gone.
   */
  let quotaWarning = false;

  /**
   * @param {import('@streamymusic/shared').AlbumDetailResponse} album
   * @returns {Promise<void>}
   */
  async function requestAlbumDownload(album) {
    const tracks = album.tracks
      .filter((track) => track.available)
      .map((track) => trackSpec(track, album));

    const result = await downloads.enqueue({
      id: album.id,
      type: 'Album',
      title: album.title,
      sub: `${album.artist.name} · ${String(tracks.length)} tracks`,
      albumId: album.id,
      tracks,
    });
    reportEnqueue(result, album.title);
  }

  /**
   * The catalog metadata one track carries into the offline store — the
   * title/artist/album/duration AC 13.2 needs to render it with no network.
   *
   * @param {import('@streamymusic/shared').AlbumTrack} track
   * @param {import('@streamymusic/shared').AlbumDetailResponse} album
   * @returns {import('../../lib/download-manager.js').DownloadTrackSpec}
   */
  function trackSpec(track, album) {
    return {
      id: track.id,
      title: track.title,
      artist: album.artist.name,
      album: album.title,
      albumId: album.id,
      durationS: track.durationS,
      format: track.format,
    };
  }

  /**
   * PRODUCT W2 — a single track's download. Same gate, same refusals, same
   * copy as the album path; only the row shape differs (`type: 'Track'`,
   * which until now was an unreachable branch of `DownloadItem`).
   *
   * @param {import('@streamymusic/shared').AlbumTrack} track
   * @param {import('@streamymusic/shared').AlbumDetailResponse} album
   * @returns {Promise<void>}
   */
  async function requestTrackDownload(track, album) {
    const result = await downloads.enqueue({
      id: track.id,
      type: 'Track',
      title: track.title,
      sub: `${album.artist.name} · ${album.title}`,
      albumId: album.id,
      tracks: [trackSpec(track, album)],
    });
    reportEnqueue(result, track.title);
  }

  /**
   * The user-visible half of the AC 13.6 gate, shared by both download paths.
   * Every refusal is surfaced — none of them is allowed to be a no-op the user
   * has to guess at.
   *
   * @param {import('../../lib/download-manager.js').EnqueueResult} result
   * @param {string} label what the user asked to download
   * @returns {void}
   */
  function reportEnqueue(result, label) {
    if (result.started) {
      quotaWarning = false;
      shell.live.toast(`Downloading “${label}”`, 'ok');
      return;
    }
    if (result.reason === 'quota') {
      // AC 13.6 — warned, and NOT started. Nothing was written and nothing
      // was requested; `enqueue` returned before either could happen.
      quotaWarning = true;
      shell.live.toast(QUOTA_WARNING_MESSAGE, 'err');
      void downloadsView?.refresh();
      return;
    }
    if (result.reason === 'duplicate') {
      shell.live.toast(`“${label}” is already in your downloads.`, 'info');
      return;
    }
    if (result.reason === 'unavailable') {
      shell.live.toast('This device can’t store downloads.', 'err');
      return;
    }
    shell.live.toast('Nothing here can be downloaded.', 'err');
  }

  /** AC 13.3 — the focus-trapped confirm, per ui-contract's dialog spec. */
  function confirmDeleteAllDownloads(runDelete) {
    shell.overlays.open(
      ConfirmModal({
        title: 'Delete all downloads?',
        body: 'This frees up all offline storage on this device. Your Drive files are untouched.',
        confirmLabel: 'Delete all',
        onConfirm: () => shell.overlays.closeTop({ then: runDelete }),
        onCancel: () => shell.overlays.closeTop(),
      }),
    );
  }

  /* ── Bounded album cache for the grid ⋯ sheets ────────────────────────
   *
   * A grid card carries only the SUMMARY, so opening its action sheet needs
   * `GET /api/albums/:id` — but five taps on the same tile must not be five
   * full-payload fetches. Small LRU of in-flight/settled promises; a
   * rejection evicts itself so a transient failure is never cached. Bounded,
   * per-page-lifetime: favourite state comes from the STORE at sheet-open
   * time, so a cached payload cannot serve a stale heart.
   */
  const ALBUM_CACHE_LIMIT = 8;
  /** @type {Map<string, Promise<import('@streamymusic/shared').AlbumDetailResponse>>} */
  const albumCache = new Map();

  /**
   * Key an album payload is persisted under in the offline catalog store.
   * Namespaced because that store is shared — `getCatalog` is keyed by an
   * opaque string and an album id could otherwise collide with some other
   * kind of cached document.
   *
   * @param {string} id
   * @returns {string}
   */
  function albumCatalogKey(id) {
    return `album:${id}`;
  }

  /**
   * The AC 13.2 offline album source, handed to {@link AlbumDetail}.
   *
   * THIS IS WHAT `putCatalog`/`getCatalog` ARE FOR. They were built by TASK
   * 10.1 and had zero production callers — documented dead code asserting a
   * purpose nothing delivered. The payload written by {@link getAlbumCached}
   * on every successful album fetch is read back here when the network is
   * gone, which is what finally makes "downloaded content plays in airplane
   * mode with title/artist/album + cover art" reachable from the UI.
   *
   * Returns `null` rather than throwing when there is no local copy: the view
   * then falls through to its ordinary error screen, which is the honest
   * outcome for an album the user never opened while online.
   *
   * @param {string} id
   * @returns {Promise<import('@streamymusic/shared').AlbumDetailResponse|null>}
   */
  /**
   * Persists an album payload for airplane-mode rendering (AC 13.2/13.8).
   *
   * Best-effort by contract ("the broader cache is best-effort"): a storage
   * fault must never fail a fetch that already succeeded, and the album screen
   * the user is looking at is unaffected either way.
   *
   * @param {import('@streamymusic/shared').AlbumDetailResponse} album
   * @returns {void}
   */
  function rememberAlbum(album) {
    void store.putCatalog(albumCatalogKey(album.id), album).catch(() => undefined);
  }

  async function offlineAlbum(id) {
    const raw = await store.getCatalog(albumCatalogKey(id));
    // PERSISTED DATA IS INPUT (offline-store.ts's own rule): another tab, an
    // older schema or a half-applied migration could have written anything, so
    // the row is VALIDATED against the same DTO the network path parses rather
    // than cast. A malformed row is treated as absent, never rendered.
    const parsed = albumDetailResponseSchema.safeParse(raw);
    return parsed.success ? parsed.data : null;
  }

  /**
   * @param {string} albumId
   * @returns {Promise<import('@streamymusic/shared').AlbumDetailResponse>}
   */
  function getAlbumCached(albumId) {
    const hit = albumCache.get(albumId);
    if (hit !== undefined) {
      // LRU refresh: re-insert so the oldest UNUSED entry evicts first.
      albumCache.delete(albumId);
      albumCache.set(albumId, hit);
      return hit;
    }
    const entry = api.getAlbum(albumId).then((album) => {
      rememberAlbum(album);
      return album;
    });
    albumCache.set(albumId, entry);
    entry.catch(() => {
      if (albumCache.get(albumId) === entry) albumCache.delete(albumId);
    });
    if (albumCache.size > ALBUM_CACHE_LIMIT) {
      const oldest = albumCache.keys().next().value;
      if (oldest !== undefined) albumCache.delete(oldest);
    }
    return entry;
  }

  /**
   * @param {{ route: { id: string }, params: Record<string, string> }} resolved
   * @param {{ element: HTMLElement, dispose: () => void }} view
   */
  function mountView(resolved, view) {
    activeView = view;
    shell.renderRoute(/** @type {any} */ (resolved), view.element);
  }

  /**
   * Optimistic favorite toggle with rollback — ui-contract §10.6 ("the heart
   * toggles on immediately, then reverts … with an announced error toast").
   * Every surface reads the flag from the store, so ONE write repaints the
   * album header and every track row at once.
   *
   * A 404 on REMOVE is success, not failure: the resource is already absent
   * for this caller (routes/favorites.ts documents 404 as "unknown,
   * already-removed, or another user's"), so rolling the heart back would
   * re-assert a favorite that does not exist.
   *
   * @param {'track'|'album'} type
   * @param {string} targetId
   * @returns {Promise<void>}
   */
  async function toggleFavorite(type, targetId) {
    const key = type === 'album' ? 'albums' : 'tracks';
    const before = getState().catalog.favorites[key];
    const had = before.includes(targetId);
    const optimistic = had ? before.filter((id) => id !== targetId) : [...before, targetId];

    setState((state) => ({
      catalog: {
        ...state.catalog,
        favorites: { ...state.catalog.favorites, [key]: optimistic },
      },
    }));
    shell.live.announce(had ? 'Removed from favorites' : 'Added to favorites');

    try {
      if (had) await api.removeFavorite(type, targetId);
      else await api.addFavorite({ targetType: type, targetId });
    } catch (error) {
      if (had && /** @type {{ status?: number }} */ (error).status === 404) return;
      setState((state) => ({
        catalog: {
          ...state.catalog,
          favorites: { ...state.catalog.favorites, [key]: before },
        },
      }));
      shell.live.toast('Couldn’t save favorite — change rolled back.', 'err');
    }
  }

  /**
   * The mock's `_play(list, i, shuf)` (L896), now driving the REAL transport.
   *
   * The engine owns the queue algebra and the audio element; this function's
   * only job is translating an album-detail selection into the engine's
   * vocabulary, including the `context_type`/`context_id` pair the play-event
   * contract requires for an album play (dto.ts `IDENTIFIED_PLAY_CONTEXT_TYPES`).
   *
   * @param {import('../views/album/AlbumDetail.js').PlayRequest} request
   */
  function startPlayback(request) {
    const { album, tracks, index, shuffle } = request;
    if (tracks.length === 0) {
      shell.live.toast('Nothing here can be played.', 'err');
      return;
    }
    const items = tracks.map((track) => ({
      id: track.id,
      title: track.title,
      artist: album.artist.name,
      album: album.title,
      albumId: album.id,
      // #8 — the album artist's id, so the player ⋯ can offer "Go to artist".
      artistId: album.artist.id,
      // The album's cover ref rides along so the mini/full player paints the
      // REAL cover (format.js `albumArtUrl` rule); art-less albums stay `null`
      // and keep the gradient.
      coverRef: album.coverRef ?? null,
      dur: track.durationS,
      // Carried so `lib/codec-detect.ts` can refuse an undecodable container
      // BEFORE a stream is requested (AC 5.7).
      format: track.format,
      formatSupported: track.formatSupported,
    }));

    player.playQueue(items, index, {
      ...(shuffle ? { shuffle: true } : {}),
      context: { type: 'album', id: album.id },
    });
    /*
     * §10.2 success toast. Under SHUFFLE the engine picks the first track, so
     * naming a track here would be a guess — the collection is named instead,
     * and the engine's own live announcement ("Now playing <title>") reports
     * the truth once the track actually starts.
     */
    if (shuffle) {
      shell.live.toast(`Shuffling “${album.title}”`, 'ok');
      return;
    }
    const started = items[Math.max(0, Math.min(index, items.length - 1))];
    if (started !== undefined) shell.live.toast(`Playing “${started.title}”`, 'ok');
  }

  /**
   * Wraps {@link startPlayback} with the §11 offline-attempt report. Only
   * offline attempts are observed — `offline_play_attempted` is the Offline
   * -success SLI, and an online play is not one of its samples.
   *
   * @param {import('../views/album/AlbumDetail.js').PlayRequest} request
   */
  function startPlaybackObserved(request) {
    startPlayback(request);
    if (!offline) return;
    const { tracks, index, shuffle } = request;
    if (tracks.length === 0) return;
    // Under SHUFFLE the engine picks the first track, so the id we could name
    // here would be a guess; the sample is dropped rather than attributed to
    // the wrong track (an SLI with invented dimensions is worse than a gap).
    if (shuffle) return;
    const started = tracks[Math.max(0, Math.min(index, tracks.length - 1))];
    /*
     * SUBSCRIBED AFTER `startPlayback`, and that ordering is safe only because
     * the engine's load is ASYNCHRONOUS — `subscribe()` does not replay the
     * current state, so a transport that reached `playing` synchronously would
     * leave this observer waiting for a transition that had already happened,
     * and the attempt would be reported `succeeded: false` at the timeout.
     *
     * Pinned here because the dependency is invisible from this file: nothing
     * in `audio-engine.ts`'s signature says the load cannot complete before
     * `playQueue` returns.
     */
    if (started !== undefined) observeOfflinePlay(started.id);
  }

  /**
   * Plays a `TrackCard` collection from a NON-album surface (favorites, search,
   * playlist). Album plays go through {@link startPlayback}, which carries the
   * album's own `context_id`; this carries the SURFACE's play context
   * (dto.ts `IDENTIFIED_PLAY_CONTEXT_TYPES` / the id-less trio) and takes the
   * already-playable list the view hands it. It wraps the §11 offline-attempt
   * observation the same way {@link startPlaybackObserved} does.
   *
   * @param {readonly import('@streamymusic/shared').TrackCard[]} tracks playable, in list order
   * @param {number} index
   * @param {{
   *   context: import('../../lib/audio-engine.js').PlayContext,
   *   label: string,
   *   shuffle?: boolean,
   * }} opts
   */
  function playCollection(tracks, index, opts) {
    if (tracks.length === 0) {
      shell.live.toast('Nothing here can be played.', 'err');
      return;
    }
    const items = tracks.map((track) => ({
      id: track.id,
      title: track.title,
      artist: track.artistName,
      album: track.albumTitle,
      albumId: track.albumId,
      // Same as the album path — the card's own cover ref so the player paints
      // the real cover; `null` keeps the gradient.
      coverRef: track.coverRef ?? null,
      dur: track.durationS,
      format: track.format,
      formatSupported: track.formatSupported,
    }));
    player.playQueue(items, index, {
      ...(opts.shuffle === true ? { shuffle: true } : {}),
      context: opts.context,
    });
    if (opts.shuffle === true) {
      shell.live.toast(`Shuffling ${opts.label}`, 'ok');
    } else {
      const started = items[Math.max(0, Math.min(index, items.length - 1))];
      if (started !== undefined) shell.live.toast(`Playing “${started.title}”`, 'ok');
    }
    // §11 offline SLI — same rule as startPlaybackObserved: shuffle picks the
    // first track, so the id we could name would be a guess; the sample is
    // dropped rather than attributed to the wrong track.
    if (offline && opts.shuffle !== true) {
      const started = tracks[Math.max(0, Math.min(index, tracks.length - 1))];
      if (started !== undefined) observeOfflinePlay(started.id);
    }
  }

  /**
   * The `/favorites` "+ QUEUE" collection control — appends the tracks to the
   * SERVER queue (`POST` via `mutateQueue({kind:'add'})`), which is what the
   * `/queue` view renders. The loaded favorites set can span multiple pages
   * (> `QUEUE_ADD_BATCH_MAX`), so send it in cap-sized batches rather than one
   * call the server would reject.
   *
   * @param {readonly import('@streamymusic/shared').TrackCard[]} tracks
   */
  function queueTracks(tracks) {
    const trackIds = tracks.map((track) => track.id);
    if (trackIds.length === 0) {
      shell.live.toast('Nothing here to queue.', 'err');
      return;
    }
    const batches = [];
    for (let i = 0; i < trackIds.length; i += QUEUE_ADD_BATCH_MAX) {
      batches.push(trackIds.slice(i, i + QUEUE_ADD_BATCH_MAX));
    }
    void (async () => {
      try {
        for (const batch of batches) {
          await api.mutateQueue({ kind: 'add', trackIds: batch });
        }
        shell.live.toast('Added to your queue.', 'ok');
      } catch {
        shell.live.toast('Couldn’t add to the queue.', 'err');
      }
    })();
  }

  /** @type {import('../views/album/AlbumDetail.js').AlbumDetailHandlers} */
  const albumHandlers = {
    onPlay: startPlaybackObserved,
    onToggleFavoriteAlbum: (album) => {
      void toggleFavorite('album', album.id);
    },
    onToggleFavoriteTrack: (track) => {
      void toggleFavorite('track', track.id);
    },
    /**
     * AC 13.5 — a refused tap is a TOAST, never a silent failure, and offline
     * it is also a §11 sample: the user asked to play something that was not
     * downloaded, and `succeeded: false` is exactly what the Offline-success
     * SLI is measuring. Only the `not-downloaded` state counts; a 410-gone or
     * format-unsupported refusal would have happened online too.
     *
     * @param {string} message
     * @param {{ state?: string, trackId?: string }} [context]
     */
    onBlocked: (message, context = {}) => {
      shell.live.toast(message, 'err');
      if (offline && context.state === 'not-downloaded' && context.trackId !== undefined) {
        reportOfflinePlay(context.trackId, false);
      }
    },
    onAlbumActions: (album) => {
      const playable = album.tracks.filter((track) => track.available && track.formatSupported);
      const favorited = getState().catalog.favorites.albums.includes(album.id);
      // §10.3: each item closes the sheet, THEN acts.
      const act = (/** @type {() => void} */ run) => () => {
        shell.overlays.closeTop({ then: run });
      };
      shell.overlays.open(
        ActionSheet({
          title: album.title,
          onDismissRequest: () => shell.overlays.closeTop(),
          items: [
            {
              icon: '▶',
              label: 'Play album',
              onSelect: act(() =>
                startPlayback({ album, tracks: playable, index: 0, shuffle: false }),
              ),
            },
            {
              icon: '⤨',
              label: 'Shuffle album',
              onSelect: act(() =>
                startPlayback({ album, tracks: playable, index: 0, shuffle: true }),
              ),
            },
            {
              // #8 — jump to the album's artist. `navigate` sets location.hash,
              // which pushes history, so the browser Back is a real back.
              icon: '→',
              label: 'Go to artist',
              onSelect: act(() => navigate(`#/artists/${encodeURIComponent(album.artist.id)}`)),
            },
            {
              // AC 13.1 — the download action. Every refusal (quota, duplicate,
              // no storage) is surfaced by `requestAlbumDownload`; none of them
              // is allowed to be a no-op the user has to guess at.
              icon: '↓',
              label: 'Download album',
              onSelect: act(() => {
                void requestAlbumDownload(album);
              }),
            },
            {
              icon: '♥',
              label: favorited ? 'Remove favorite' : 'Favorite',
              onSelect: act(() => {
                void toggleFavorite('album', album.id);
              }),
            },
          ],
        }),
      );
    },
    onTrackActions: (track, album) => {
      const favorited = getState().catalog.favorites.tracks.includes(track.id);
      const act = (/** @type {() => void} */ run) => () => {
        shell.overlays.closeTop({ then: run });
      };
      /** @type {Array<{icon: string, label: string, onSelect: () => void}>} */
      const items = [
        {
          icon: '♥',
          label: favorited ? 'Unfavorite' : 'Favorite',
          onSelect: act(() => {
            void toggleFavorite('track', track.id);
          }),
        },
      ];

      /*
       * #8 — jump to the album or the artist. `navigate` sets location.hash,
       * which pushes history, so Back is a real back.
       *
       * "Go to album" only when this sheet was NOT opened FROM an album page
       * (`album === undefined`, i.e. from the full player) — on the album page
       * you are already there. The album's own `artistId` is preferred, falling
       * back to the album context when the sheet has one.
       */
      const targetAlbumId = album?.id ?? track.albumId;
      const targetArtistId = track.artistId ?? album?.artist.id;
      if (album === undefined && targetAlbumId !== undefined) {
        items.push({
          icon: '→',
          label: 'Go to album',
          onSelect: act(() => navigate(`#/albums/${encodeURIComponent(targetAlbumId)}`)),
        });
      }
      if (targetArtistId !== undefined) {
        items.push({
          icon: '→',
          label: 'Go to artist',
          onSelect: act(() => navigate(`#/artists/${encodeURIComponent(targetArtistId)}`)),
        });
      }
      /*
       * PRODUCT W2 — the single-track download.
       *
       * `DOWNLOADS_EMPTY_MESSAGE` is the mock's copy verbatim: 'Use "Download"
       * on any album, playlist or track to keep it offline.' Only the ALBUM
       * sheet had a Download item, so the first thing a new user read on an
       * empty Downloads screen was guidance for two affordances that did not
       * exist — and `DownloadItem.type === 'Track'` plus `tagBackground`'s
       * purple branch were unreachable dead paths.
       *
       * Adding the affordance rather than shrinking the copy, because `enqueue`
       * already accepts a one-track request and this is also what makes W1's
       * per-track download truth load-bearing: with single-track downloads
       * real, an album roll-up applied to a track row would mark nine
       * un-downloaded tracks playable offline. (Playlists remain unwired —
       * see the empty-state copy, which now says only what is true.)
       *
       * Guarded on `album`: a track reached from the FULL PLAYER carries only
       * `{id, title}` (see `AlbumDetailHandlers.onTrackActions`), and a
       * download needs the artist/album/duration/format the offline catalog
       * stores for airplane-mode playback. Offering an item that could only
       * fail would be worse than not offering it.
       */
      if (album !== undefined && track.durationS !== undefined) {
        items.push({
          icon: '↓',
          label: 'Download track',
          onSelect: act(() => {
            void requestTrackDownload(track, album);
          }),
        });
      }
      shell.overlays.open(
        ActionSheet({
          title: track.title,
          onDismissRequest: () => shell.overlays.closeTop(),
          items,
        }),
      );
    },
  };

  /**
   * Renders one of Story 8's three catalog routes, or returns false so the
   * caller falls through to the default heading-only body.
   *
   * @param {ReturnType<typeof import('./router.js').resolve>} resolved
   * @returns {boolean}
   */
  function renderCatalogRoute(resolved) {
    const heading = PageHeading(resolved.route);
    const state = getState();
    /*
     * #back — how deep each paginated catalog list was left. Keyed per SURFACE
     * (not per full hash) so `?query` noise cannot fragment the memory. Only
     * the STATIC catalog surfaces get one: their pages are already cached for
     * 24h per cursor, so replaying the depth is free, and a static library
     * cannot go stale underneath it. The mutable surfaces (favorites,
     * playlists, queue, search) deliberately keep re-fetching — a remembered
     * page of hearts or queue rows would be a stale read, which is worse than
     * a short list.
     */
    const depthFor = (/** @type {string} */ key) => listDepth.for(key);

    if (resolved.route.id === 'browse-artists') {
      mountView(
        resolved,
        ArtistsList({
          api,
          heading,
          isAdmin: state.session.role === 'admin',
          depth: depthFor('artists'),
          announce: shell.live.announce,
        }),
      );
      return true;
    }
    if (resolved.route.id === 'browse-albums') {
      mountView(
        resolved,
        AlbumsList({
          api,
          heading,
          getState,
          subscribe,
          isAdmin: state.session.role === 'admin',
          offline,
          downloadedAlbums,
          onAlbumMenu: (card) => {
            void getAlbumCached(card.id)
              .then((album) => albumHandlers.onAlbumActions(album))
              .catch(() => shell.live.toast('Couldn’t open album actions.', 'err'));
          },
          depth: depthFor('albums'),
          announce: shell.live.announce,
          warn,
        }),
      );
      return true;
    }
    if (resolved.route.id === 'downloads') {
      /*
       * AC 13.8 — this route reads ONLY the local store, never the network,
       * so it renders identically in airplane mode. The confirm dialog and
       * the quota latch stay out here: the view owns markup, the root owns
       * the overlay stack and the cross-screen state.
       */
      const view = DownloadsView({
        manager: downloads,
        heading,
        onDeleteAllRequest: confirmDeleteAllDownloads,
        quotaWarning: () => quotaWarning || resolved.query.get('state') === 'quota-warning',
        announce: shell.live.announce,
        toast: shell.live.toast,
        warn,
      });
      downloadsView = view;
      mountView(resolved, view);
      return true;
    }
    if (resolved.route.id === 'artist') {
      const artistId = resolved.params.artistId ?? '';
      mountView(
        resolved,
        ArtistDetail({
          api,
          heading,
          artistId,
          getState,
          subscribe,
          offline,
          downloadedAlbums,
          depth: depthFor(`artist:${artistId}`),
          announce: shell.live.announce,
          // The app's ONE diagnostics channel. A server contract violation
          // spotted while deciding an availability marker must not stop at the
          // end user's devtools console — see AvailabilityBadge.js.
          warn,
          onAlbumMenu: (card) => {
            // The grid card only carries the SUMMARY; the sheet needs the
            // album's tracks, so it is fetched on demand rather than eagerly
            // for every tile in the grid — through the bounded cache, so
            // re-opening the same tile's sheet costs no second fetch.
            void getAlbumCached(card.id)
              .then((album) => albumHandlers.onAlbumActions(album))
              .catch(() => shell.live.toast('Couldn’t open album actions.', 'err'));
          },
        }),
      );
      return true;
    }
    if (resolved.route.id === 'album') {
      mountView(
        resolved,
        AlbumDetail({
          api,
          heading,
          albumId: resolved.params.albumId ?? '',
          handlers: albumHandlers,
          getState,
          subscribe,
          offline,
          downloadedAlbums,
          // W1 — track rows ask about THEMSELVES, not about their album.
          downloadedTracks,
          // C3 — the AC 13.2 airplane-mode fallback, and the write that
          // makes it possible.
          offlineAlbum,
          onAlbumLoaded: rememberAlbum,
          // #back — the album's back arrow returns to wherever the user came
          // from (a genre, search, favorites…), not always to the artist.
          canGoBack,
          onBack: () => {
            win.history.back();
          },
          announce: shell.live.announce,
          warn,
        }),
      );
      return true;
    }

    /*
     * Story 10 surfaces (home / stats / genres / admin). Each view returns the
     * same `{ element, dispose }` seam as the Story-8 catalog views, so they
     * ride the same `mountView` disposal path — no listener or in-flight fetch
     * outlives its section on the next navigation.
     *
     * A grid card carries only the album SUMMARY, so its "⋯" sheet needs
     * `GET /api/albums/:id` — fetched on demand through the same bounded
     * `getAlbumCached` the artist grid uses (re-opening a tile costs no second
     * fetch, and a cached payload never serves a stale ♥).
     */
    const openAlbumMenu = (card) => {
      void getAlbumCached(card.id)
        .then((album) => albumHandlers.onAlbumActions(album))
        .catch(() => shell.live.toast('Couldn’t open album actions.', 'err'));
    };

    if (resolved.route.id === 'home') {
      mountView(
        resolved,
        HomeView({
          api,
          heading,
          handlers: {
            onAlbumMenu: openAlbumMenu,
            // An ON REPEAT row PLAYS (the FavoritesView idiom): the tapped
            // track inside the rail's playable slice. The context is the BARE
            // one — this rail is none of the five dto.ts play-context types,
            // and a standalone play is exactly what feeds it (dto.ts
            // `recentlyPlayedTrackEntrySchema`).
            onPlayTrack: (track, tracks) => {
              const index = Math.max(
                0,
                tracks.findIndex((candidate) => candidate.id === track.id),
              );
              playCollection(tracks, index, { context: {}, label: 'your recent spins' });
            },
            onBlocked: albumHandlers.onBlocked,
          },
          isAdmin: state.session.role === 'admin',
          offline,
          downloadedAlbums,
          getState,
          announce: shell.live.announce,
          warn,
        }),
      );
      return true;
    }
    if (resolved.route.id === 'stats') {
      mountView(
        resolved,
        StatsView({
          api,
          heading,
          // #back — the period toggle is in-page state, not a route, so a naive
          // Back would drop the user back on ALL TIME. Seed it from `?period`
          // and record each change there (replaceState — no history spam, no
          // re-mount) so returning to /stats restores the same period.
          initialPeriod:
            resolved.query.get('period') === 'last_30_days' ? 'last_30_days' : 'all_time',
          onPeriodChange: (nextPeriod) => {
            const target = nextPeriod === 'last_30_days' ? '#/stats?period=last_30_days' : '#/stats';
            win.history.replaceState(win.history.state, '', target);
          },
          // ui-contract §3 — a ranked Top-Songs row opens the track menu. A
          // stats entry carries only { id, title } (no album detail), which is
          // exactly the reduced shape `onTrackActions` accepts from the full
          // player, so no album-download item is offered.
          handlers: {
            onSongRow: (entry) =>
              albumHandlers.onTrackActions({ id: entry.track.id, title: entry.track.title }),
          },
          announce: shell.live.announce,
        }),
      );
      return true;
    }
    if (resolved.route.id === 'browse-genres') {
      mountView(
        resolved,
        GenresList({
          api,
          heading,
          isAdmin: state.session.role === 'admin',
          depth: depthFor('genres'),
          announce: shell.live.announce,
        }),
      );
      return true;
    }
    if (resolved.route.id === 'genre') {
      mountView(
        resolved,
        GenrePage({
          api,
          heading,
          genreId: resolved.params.genreId ?? '',
          getState,
          subscribe,
          offline,
          downloadedAlbums,
          onAlbumMenu: openAlbumMenu,
          depth: depthFor(`genre:${resolved.params.genreId ?? ''}`),
          announce: shell.live.announce,
          warn,
        }),
      );
      return true;
    }
    if (resolved.route.id === 'admin-users') {
      mountView(
        resolved,
        UsersView({
          api,
          heading,
          overlays: shell.overlays,
          // The self-row guard compares each row's linked account id against
          // the session's — never for display (GET /api/me carries no email).
          selfUserId: state.session.userId ?? null,
          toast: shell.live.toast,
          announce: shell.live.announce,
        }),
      );
      return true;
    }
    if (resolved.route.id === 'admin-sync') {
      mountView(
        resolved,
        SyncView({
          api,
          heading,
          toast: shell.live.toast,
          announce: shell.live.announce,
        }),
      );
      return true;
    }
    if (resolved.route.id === 'search') {
      mountView(
        resolved,
        SearchView({
          api,
          heading,
          // AC 11.1 — a song result PLAYS (it does not navigate). One track,
          // stamped with the `search` play context (id FORBIDDEN — the id-less
          // trio of PlayContext).
          onPlayTrack: (track) =>
            playCollection([track], 0, {
              context: { type: 'search' },
              label: `“${track.title}”`,
            }),
          // The client holds no genre table; the store's catalog genres feed
          // the AC 11.9 chips (the view caps them and drops "Unknown").
          genres: /** @type {import('@streamymusic/shared').CatalogRef[]} */ (state.catalog.genres),
          onBlocked: albumHandlers.onBlocked,
          offline,
          downloadedAlbums,
          downloadedTracks,
          initialQuery: resolved.query.get('q') ?? '',
          ...(resolved.query.get('genre') === null
            ? {}
            : { initialGenreId: /** @type {string} */ (resolved.query.get('genre')) }),
          // #back — keep the live query AND genre filter in the URL (replaceState,
          // so no history spam and no re-mount) so tapping a result then pressing
          // Back lands on the same results, seeded via `initialQuery`/
          // `initialGenreId` above.
          onQueryChange: (query, genreId) => {
            let target = '#/search';
            if (query !== '') {
              const params = new URLSearchParams({ q: query });
              if (genreId !== null) params.set('genre', genreId);
              target = `#/search?${params.toString()}`;
            }
            win.history.replaceState(win.history.state, '', target);
          },
          announce: shell.live.announce,
          warn,
        }),
      );
      return true;
    }
    if (resolved.route.id === 'favorites') {
      mountView(
        resolved,
        FavoritesView({
          api,
          heading,
          // AC 10.2 — the collection controls hand the PLAYABLE list out; the
          // root owns the transport and the `favorites` play context.
          handlers: {
            onPlayAll: (tracks) =>
              playCollection(tracks, 0, {
                context: { type: 'favorites' },
                label: 'your favorites',
              }),
            onShuffle: (tracks) =>
              playCollection(tracks, 0, {
                context: { type: 'favorites' },
                label: 'your favorites',
                shuffle: true,
              }),
            onQueue: queueTracks,
            onPlayTrack: (track, tracks) => {
              const index = Math.max(
                0,
                tracks.findIndex((candidate) => candidate.id === track.id),
              );
              playCollection(tracks, index, {
                context: { type: 'favorites' },
                label: 'your favorites',
              });
            },
            onToggleFavoriteTrack: (track) => {
              void toggleFavorite('track', track.id);
            },
            // A favorites TRACK row carries only { id, title } into the sheet —
            // the reduced shape onTrackActions accepts from the full player, so
            // no album-download item is offered (its metadata isn't loaded).
            onTrackActions: (track) =>
              albumHandlers.onTrackActions({ id: track.id, title: track.title }),
            // A favorites ALBUM card is a summary; its "⋯" sheet fetches the
            // album on demand through the same bounded cache the grids use.
            onAlbumActions: (album) => openAlbumMenu(album),
            onBlocked: albumHandlers.onBlocked,
          },
          getState,
          // The favorites read is authoritative about what is favorited; the
          // view MERGES the fetched ids into catalog.favorites through this seam.
          setState,
          subscribe,
          offline,
          downloadedAlbums,
          announce: shell.live.announce,
          warn,
        }),
      );
      return true;
    }
    if (resolved.route.id === 'playlists') {
      mountView(
        resolved,
        PlaylistsList({
          api,
          heading,
          overlays: shell.overlays,
          announce: shell.live.announce,
          notify: (message) => shell.live.toast(message, 'ok'),
        }),
      );
      return true;
    }
    if (resolved.route.id === 'playlist') {
      const playlistId = resolved.params.playlistId ?? '';
      mountView(
        resolved,
        PlaylistDetail({
          api,
          heading,
          playlistId,
          overlays: shell.overlays,
          handlers: {
            onPlay: (tracks, index, shuffle) =>
              playCollection(tracks, index, {
                context: { type: 'playlist', id: playlistId },
                label: 'this playlist',
                ...(shuffle ? { shuffle: true } : {}),
              }),
            onBlocked: albumHandlers.onBlocked,
            // The detail view must not import the router; a delete returns to
            // the list from here. REPLACE, not push (#back): the deleted
            // playlist's detail entry must not stay reachable by Back (it would
            // re-fetch and 404) — replacing it drops it from history.
            onDeleted: () => router.redirect('#/playlists'),
          },
          offline,
          downloadedTracks,
          announce: shell.live.announce,
          notify: (message) => shell.live.toast(message, 'ok'),
          warn,
        }),
      );
      return true;
    }
    if (resolved.route.id === 'queue') {
      mountView(
        resolved,
        QueueView({
          api,
          heading,
          overlays: shell.overlays,
          handlers: {
            // The view validates + trims the name; the root owns the
            // `POST /api/playlists/from-queue` call and surfaces its outcome.
            onSaveQueueAsPlaylist: (name) => {
              void api.createPlaylistFromQueue(name).then(
                (summary) => shell.live.toast(`Playlist “${summary.name}” created`, 'ok'),
                () => shell.live.toast('Couldn’t save the queue as a playlist.', 'err'),
              );
            },
          },
          // The tabs are LINKS to `#/queue?tab=…`; the active tab is the query.
          tab: resolved.query.get('tab') === 'history' ? 'history' : 'queue',
          announce: shell.live.announce,
        }),
      );
      return true;
    }
    return false;
  }

  const router = createRouter({
    target: win,
    onNavigate: (resolved) => {
      // A real route change supersedes any overlay that is still standing.
      // Silent: the hash write already moved history past their sentinels.
      shell.overlays.closeAllSilently();

      // One more route of ours is on screen — see `renderedRoutes` (#back).
      renderedRoutes += 1;

      // Release the previous route's view BEFORE anything else can render.
      if (activeView !== null) {
        activeView.dispose();
        activeView = null;
      }
      // The downloads view is ALSO held by the manager's progress callback;
      // dropping the reference here is what stops a finished transfer
      // repainting a section the user has navigated away from.
      downloadsView = null;

      setState({
        route: {
          path: resolved.path,
          query: resolved.queryString,
          hash: resolved.hash,
          routeId: resolved.route.id,
        },
      });

      /*
       * AC 1.1 — the auth guard. Unauthenticated visits to ANY shell route
       * bounce to `/signin`, carrying an allowlist-validated `returnTo`
       * (views/auth/guard.js). An authenticated visit to a sign-in screen
       * bounces the other way.
       */
      const decision = guardRoute({
        route: resolved.route,
        hash: resolved.hash,
        session: getState().session,
        win,
      });
      if (decision.redirectTo !== null) {
        // Replace, not push (#back): the guarded entry must not linger as a
        // Back-trap that bounces the user straight back to /signin.
        router.redirect(decision.redirectTo);
        return;
      }

      // Shell-less auth screens (ui-contract §2).
      if (resolved.route.shell === false) {
        shell.renderStandalone(AuthScreen(resolved, win));
        return;
      }

      /*
       * Cosmetic admin bounce. NOT an authorization control: every admin
       * capability is enforced by `requireAdmin` on the server
       * (apps/api/src/routes/admin-*.ts). This only spares a member a page of
       * 403s.
       */
      if (resolved.route.adminOnly === true && getState().session.role !== 'admin') {
        // Replace, not push (#back): otherwise Back re-enters the admin route
        // and bounces here again, trapping the member.
        router.redirect('#/not-authorized');
        return;
      }

      if (resolved.route.id === 'settings') {
        shell.renderRoute(
          resolved,
          Settings({
            session: getState().session,
            // Makes "keep the screen awake" verifiable on the device instead of
            // only in a console the phone never shows.
            wakeLockStatus: () => wakeLock.status(),
            doc,
          }),
        );
        return;
      }

      // Story 8: /browse/artists, /artists/:id, /albums/:id.
      if (renderCatalogRoute(resolved)) return;

      shell.renderRoute(resolved);
    },
  });

  // 3. PATH deep link -> hash route, before the router reads location.hash.
  normalizeEntryPath(win);

  if (seed) await seedFromDevFixture();

  // 4. Real session.
  if (loadSession) {
    const status = await loadServerSession(api);
    // 5. Land on wherever the user was headed before the Google round-trip —
    //    only when the URL carries no destination of its own.
    if (status === 'authenticated') {
      const hash = win.location.hash;
      if (hash === '' || hash === '#' || hash === '#/') {
        const pending = consumeReturnTo(win);
        if (pending !== null && pending !== '/') win.location.hash = pending;
      }
      // Warm the catalog through the documented store seam (modules/app/README
      // "Where the api-client plugs in"). Non-fatal: a home-page failure must
      // not stop the app from booting into a usable shell.
      try {
        const page = await api.getHome();
        // `homeResponseSchema` models each rail as a discriminated union
        // (ok | empty | error) — only `ok` carries `items` (README's sketch
        // predates the DTO; the DTO is the contract).
        const rail = page.recentlyAdded;
        if (rail.status === 'ok') {
          setState({ catalog: { ...getState().catalog, albums: rail.items } });
        }
      } catch {
        /* the shell renders empty; Stories 8-10 own the per-section error UI */
      }
    }
  }

  /*
   * 5b. Offline state, before the first route renders.
   *
   * ── THE AWAIT IS DELIBERATE, AND THE OLD COMMENT WAS THE DEFECT (W5) ────
   * This block used to read "All three are best-effort and NONE BLOCKS THE
   * ROUTER" directly above an `await`, which was simply false: the first call
   * opens the offline database, enumerates the whole audio cache and `getAll`s
   * the track store before `router.start()` can run.
   *
   * The honest fix is the comment, not the await — because the await is
   * load-bearing. Catalog views read `downloadedAlbums`/`downloadedTracks` AT
   * MOUNT. Firing this and forgetting it means a cold start in airplane mode
   * renders its FIRST route with an empty download set, so every album the
   * user actually has on the device is marked "⌁ NOT DOWNLOADED" and refuses
   * to play until they navigate somewhere and back — which is precisely the
   * AC 13.5 failure the download set was built to end, reintroduced at exactly
   * the moment (cold, offline) the feature exists for.
   *
   * So: ONE await, on the one thing whose absence is user-visible on the first
   * frame, and the other two genuinely do not block. `refreshDownloadSets`
   * swallows its own failures, so a wedged IndexedDB still boots into a usable
   * shell — it just boots with no badges, which is the safe default.
   */
  await refreshDownloadSets();
  // These two really are fire-and-forget: nothing on the first frame reads
  // either of them.
  void flushOfflinePlayEvents();
  void downloads.resume();

  // 6. Routing.
  router.start();
  shell.syncState(getState());
  setState({ ready: true });

  return {
    shell,
    router,
    /**
     * What this engine promises for BACKGROUND auto-advance (TASK 8.4). Read
     * by the real-device checklist in docs/dev_plans/ios-spike-report.md.
     */
    mediaTier: media.autoAdvanceTier,
    /** The download queue, for the suites that drive it directly. */
    downloads,
    dispose() {
      uninstallTelemetry();
      unsubscribe();
      win.removeEventListener('online', handleOnline);
      win.removeEventListener('offline', handleOffline);
      uninstallDownloads();
      cancelOfflineObserver?.();
      cancelOfflineObserver = null;
      downloadsView = null;
      // Restore whatever the seams held before this app instance, so two
      // sequential mounts in one page (every test file) cannot leak into
      // each other.
      setTelemetryBuffer(restoreTelemetryBuffer);
      setOfflinePlayEventSink(restorePlayEventSink);
      setPrePurgeQuiesce(restorePrePurgeQuiesce);
      if (coalesceTimer !== null) {
        win.clearTimeout(coalesceTimer);
        coalesceTimer = null;
      }
      wakeLock.dispose();
      media.dispose();
      player.dispose();
      if (activeView !== null) {
        activeView.dispose();
        activeView = null;
      }
      router.stop();
      shell.dispose();
    },
  };
}

/**
 * Populates the store from the DEV-only parity fixture so `pnpm dev` renders a
 * populated shell (and so the parity gate has deterministic data).
 *
 * PRODUCTION EXCLUSION: the call site below is inside `if (import.meta.env.DEV)`
 * and the import is DYNAMIC, so Vite's `false` substitution lets Rollup drop
 * the branch, the import and the chunk. The TASK 1.6 lane greps the built
 * bundle for `makeDB` / `mock-parity-fixture` and fails if either appears.
 *
 * @returns {Promise<boolean>} whether the fixture was loaded
 */
async function seedFromDevFixture() {
  if (!import.meta.env.DEV) return false;

  const fixture = await import('../../dev/mock-parity-fixture.js');
  const db = fixture.makeDB();
  const current = db.queue[0] ?? null;

  setState({
    // A dev session so the shell paints the authenticated tree. Real sessions
    // arrive from the server (loadServerSession); nothing here reaches prod.
    session: {
      authenticated: true,
      role: 'admin',
      user: { email: 'you@steamymusic.app', name: 'You (Owner)', initial: 'Y' },
    },
    catalog: {
      albums: db.albums,
      artists: db.artists,
      genres: db.genres,
      playlists: db.playlists,
      downloads: db.downloads,
      history: db.history,
      favorites: { tracks: db.favTracks, albums: db.favAlbums },
    },
    playback: {
      current,
      queue: db.queue.slice(1),
      past: [],
      playing: false,
      pos: 0,
      dur: current === null ? 0 : current.dur,
      buffering: false,
      shuffle: false,
      repeat: 'off',
      banner: null,
    },
  });
  return true;
}
